import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box, Typography, Paper, Grid, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Button, Chip,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import PublicIcon from "@mui/icons-material/Public";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import TodayIcon from "@mui/icons-material/Today";

const API_URL = process.env.REACT_APP_API_URL;

const dark = {
  bg: "#1a1a2e", card: "#16213e", border: "#394867",
  text: "#e0e0e0", textSecondary: "#9e9e9e", textMuted: "#616161",
};

const StatCard = ({ value, label, color, icon }) => (
  <Paper sx={{ p: 2, backgroundColor: dark.card, borderRadius: 2, border: `1px solid ${dark.border}`, textAlign: "center" }}>
    <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
    <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{typeof value === "number" ? value.toLocaleString() : value}</Typography>
    <Typography variant="body2" sx={{ color: dark.textSecondary, fontSize: "0.75rem" }}>{label}</Typography>
  </Paper>
);

const UsersGuestsSection = () => {
  const [stats, setStats] = useState(null);
  const [guests, setGuests] = useState([]);
  const [guestPage, setGuestPage] = useState(1);
  const [guestTotal, setGuestTotal] = useState(0);
  const [guestTotalPages, setGuestTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchGuests(); }, [guestPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/stats`);
      setStats(response.data.data);
    } catch (err) { console.error("Failed to fetch stats:", err); }
    finally { setLoading(false); }
  };

  const fetchGuests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/guests?page=${guestPage}&limit=20`);
      setGuests(response.data.data.guests);
      setGuestTotal(response.data.data.total);
      setGuestTotalPages(response.data.data.totalPages);
    } catch (err) { console.error("Failed to fetch guests:", err); }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const parseBrowser = (ua) => {
    if (!ua) return "Unknown";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    return "Other";
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: "#fff" }} /></Box>;
  if (!stats) return <Typography sx={{ color: dark.textSecondary }}>Failed to load stats.</Typography>;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ color: "#fff", mb: 3 }}>Users & Guests</Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <StatCard value={stats.totalGuests} label="Total Guests" color="#4caf50" icon={<PeopleIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={stats.totalUsers} label="Registered Users" color="#2196f3" icon={<PeopleIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={stats.totalGuestQueries} label="Total Queries" color="#ff9800" icon={<QuestionAnswerIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={stats.totalSessions} label="Sessions" color="#9c27b0" icon={<PublicIcon />} />
        </Grid>
      </Grid>

      {/* Activity Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={4} md={4}>
          <Paper sx={{ p: 2, backgroundColor: dark.card, borderRadius: 2, border: `1px solid ${dark.border}`, textAlign: "center" }}>
            <Box sx={{ color: "#66bb6a", mb: 0.5 }}><TodayIcon /></Box>
            <Typography variant="h5" sx={{ color: "#66bb6a", fontWeight: 700 }}>{stats.guestsToday.toLocaleString()}</Typography>
            <Typography variant="body2" sx={{ color: dark.textSecondary, fontSize: "0.75rem" }}>Today</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4} md={4}>
          <Paper sx={{ p: 2, backgroundColor: dark.card, borderRadius: 2, border: `1px solid ${dark.border}`, textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#42a5f5", fontWeight: 700 }}>{stats.guestsThisWeek.toLocaleString()}</Typography>
            <Typography variant="body2" sx={{ color: dark.textSecondary, fontSize: "0.75rem" }}>This Week</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4} md={4}>
          <Paper sx={{ p: 2, backgroundColor: dark.card, borderRadius: 2, border: `1px solid ${dark.border}`, textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#ab47bc", fontWeight: 700 }}>{stats.guestsThisMonth.toLocaleString()}</Typography>
            <Typography variant="body2" sx={{ color: dark.textSecondary, fontSize: "0.75rem" }}>This Month</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Daily Trend */}
      {stats.dailyTrend && stats.dailyTrend.length > 0 && (
        <Paper sx={{ p: 2, backgroundColor: dark.card, borderRadius: 2, border: `1px solid ${dark.border}`, mb: 4 }}>
          <Typography variant="subtitle2" sx={{ color: dark.textSecondary, mb: 2, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1 }}>Daily Guests (14 days)</Typography>
          <Box display="flex" alignItems="flex-end" gap={0.5} sx={{ height: 100 }}>
            {stats.dailyTrend.map((d) => {
              const max = Math.max(...stats.dailyTrend.map((t) => parseInt(t.count)));
              const height = max > 0 ? (parseInt(d.count) / max) * 80 + 20 : 20;
              return (
                <Box key={d.date} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Typography sx={{ fontSize: "0.55rem", color: dark.textSecondary, mb: 0.5 }}>{parseInt(d.count)}</Typography>
                  <Box sx={{ width: "100%", height: `${height}%`, backgroundColor: "#1565c0", borderRadius: "2px 2px 0 0", minHeight: 4 }} />
                  <Typography sx={{ fontSize: "0.5rem", color: dark.textMuted, mt: 0.5 }}>
                    {new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Top Countries & Cities side by side */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, backgroundColor: dark.card, borderRadius: 2, border: `1px solid ${dark.border}` }}>
            <Typography variant="subtitle2" sx={{ color: dark.textSecondary, mb: 1.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1 }}>Top Countries</Typography>
            {stats.byCountry.map((c, i) => (
              <Box key={i} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.5, borderBottom: i < stats.byCountry.length - 1 ? `1px solid ${dark.border}` : "none" }}>
                <Typography sx={{ color: dark.text, fontSize: "0.85rem" }}>{c.country || "Unknown"}</Typography>
                <Chip label={parseInt(c.count).toLocaleString()} size="small" sx={{ backgroundColor: dark.bg, color: dark.textSecondary, fontSize: "0.7rem" }} />
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, backgroundColor: dark.card, borderRadius: 2, border: `1px solid ${dark.border}` }}>
            <Typography variant="subtitle2" sx={{ color: dark.textSecondary, mb: 1.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1 }}>Top Cities</Typography>
            {stats.byCities.map((c, i) => (
              <Box key={i} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 0.5, borderBottom: i < stats.byCities.length - 1 ? `1px solid ${dark.border}` : "none" }}>
                <Typography sx={{ color: dark.text, fontSize: "0.85rem" }}>{c.city}{c.region ? `, ${c.region}` : ""}</Typography>
                <Chip label={parseInt(c.count).toLocaleString()} size="small" sx={{ backgroundColor: dark.bg, color: dark.textSecondary, fontSize: "0.7rem" }} />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Guests Table */}
      <Typography variant="subtitle2" sx={{ color: dark.textSecondary, mb: 1.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1 }}>
        Recent Guests ({guestTotal.toLocaleString()} total)
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: dark.card, border: `1px solid ${dark.border}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: dark.bg }}>
              {["ID", "Location", "ISP", "Browser", "Date"].map((h) => (
                <TableCell key={h} sx={{ color: dark.textSecondary, fontWeight: 600, borderBottom: `1px solid ${dark.border}`, fontSize: "0.75rem", textTransform: "uppercase" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {guests.map((g) => (
              <TableRow key={g.id} sx={{ "&:hover": { backgroundColor: "#0f3460" + "44" }, "& td": { borderBottom: `1px solid ${dark.border}` } }}>
                <TableCell sx={{ color: dark.textMuted, fontSize: "0.8rem" }}>#{g.id}</TableCell>
                <TableCell sx={{ color: dark.text, fontSize: "0.8rem" }}>
                  {g.city ? `${g.city}, ${g.region || ""}, ${g.country || ""}` : g.ip_address}
                </TableCell>
                <TableCell sx={{ color: dark.textSecondary, fontSize: "0.75rem" }}>{g.org || "-"}</TableCell>
                <TableCell sx={{ color: dark.textSecondary, fontSize: "0.8rem" }}>{parseBrowser(g.user_agent)}</TableCell>
                <TableCell sx={{ color: dark.textSecondary, fontSize: "0.75rem" }}>{formatDate(g.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" gap={2} mt={2}>
        <Button size="small" disabled={guestPage <= 1} onClick={() => setGuestPage(guestPage - 1)} sx={{ color: dark.textSecondary }}>Previous</Button>
        <Typography sx={{ color: dark.textSecondary, alignSelf: "center", fontSize: "0.8rem" }}>Page {guestPage} of {guestTotalPages}</Typography>
        <Button size="small" disabled={guestPage >= guestTotalPages} onClick={() => setGuestPage(guestPage + 1)} sx={{ color: dark.textSecondary }}>Next</Button>
      </Box>
    </Box>
  );
};

export default UsersGuestsSection;
